// src/slices/notifications/thunk.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getNotifications,
  getNotificationUnreadCount,
  markNotificationAsRead as markNotificationAsReadApi,
  markAllNotificationsAsRead as markAllNotificationsAsReadApi,
} from '../../helpers/fakebackend_helper';
import { NotificationItem } from './types';

interface FetchNotificationsParams {
  page?: number;
  pageSize?: number;
  isRead?: boolean;
  type?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Lấy danh sách thông báo của user hiện tại (mặc định 50 item mới nhất để
 * đủ dữ liệu lọc theo tab All/Khẩn cấp/Ưu tiên cao ở NotificationDropdown)
 *
 * Lưu ý: API thực tế trả thẳng { items, totalItems, pageIndex, pageSize, totalPages },
 * khác với dạng { success, data, pagination } mô tả trong api-design.md, nên đọc cả
 * hai kiểu để phòng trường hợp backend đổi lại theo đúng design sau này.
 */
export const fetchNotifications = createAsyncThunk(
  'Notification/fetchNotifications',
  async (params: FetchNotificationsParams | undefined, { rejectWithValue }) => {
    try {
      const response: any = await getNotifications({ page: 1, pageSize: 50, ...params });
      const items: NotificationItem[] = response?.items || response?.data || [];
      const totalCount: number =
        response?.totalItems ?? response?.pagination?.totalCount ?? items.length;
      return { items, totalCount };
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Không thể tải danh sách thông báo');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'Notification/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await getNotificationUnreadCount();
      return response?.unreadCount ?? response?.data?.unreadCount ?? 0;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Không thể tải số thông báo chưa đọc');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'Notification/markNotificationRead',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await markNotificationAsReadApi(id);
      return id;
    } catch (error: any) {
      // ALREADY_READ (400) không phải lỗi thật sự - vẫn cập nhật UI như đã đọc
      if (error?.data?.error?.code === 'ALREADY_READ') {
        return id;
      }
      return rejectWithValue(error?.message || 'Không thể đánh dấu đã đọc');
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'Notification/markAllNotificationsRead',
  async (_, { rejectWithValue }) => {
    try {
      await markAllNotificationsAsReadApi();
      return true;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Không thể đánh dấu tất cả đã đọc');
    }
  }
);
